import { adminFetch } from './adminFetch';

export type Campaign = {
  id: number;
  name: string;
  subjectA: string;
  subjectB?: string | null;
  htmlA?: string | null;
  htmlB?: string | null;
  templateId?: number | null;
  segment: string;
  status: string;
  testPercent: number;
  waitHours: number;
  winnerVariant?: string | null;
  winnerCriteria: string;
  recipientLimit: number;
  createdAt: string;
  scheduledAt?: string | null;
  sendingStartedAt?: string | null;
  completedAt?: string | null;
  createdBy?: string | null;
  dryRun: boolean;
};

export type CampaignListItem = {
  id: number;
  name: string;
  status: string;
  segment: string;
  sent: number;
  opened: number;
  clicked: number;
  openRate: number;
  clickRate: number;
  bounced: number;
  complained: number;
  createdAt: string;
  scheduledAt?: string | null;
  completedAt?: string | null;
  winnerVariant?: string | null;
};

export type EngagementOverview = {
  days: number;
  campaigns: number;
  sent: number;
  opened: number;
  clicked: number;
  bounced: number;
  complained: number;
  openRate: number;
  clickRate: number;
  bounceRate: number;
  complaintRate: number;
};

export type SendsOverTimePoint = { day: string; sends: number };

export type SchedulerStatus = {
  stream: string;
  label: string;
  lastRunUtc?: string | null;
  runs: number;
};

export type AudienceHealth = {
  confirmedWithEmail: number;
  sendable: number;
  suppressed: number;
  optedOutAnnouncements: number;
  optedOutMarketing: number;
};

export type SegmentReach = {
  segment: string;
  campaigns: number;
  lastSentAt?: string | null;
  sent: number;
  opened: number;
};

export type CampaignStats = {
  totalRecipients: number;
  variantA: VariantStats;
  variantB?: VariantStats | null;
  winnerVariant?: string | null;
  winnerCriteria?: string | null;
};

export type VariantStats = {
  variant: string;
  sent: number;
  opened: number;
  clicked: number;
  bounced: number;
  complained: number;
  openRate: number;
  clickRate: number;
  bounceRate: number;
  complaintRate: number;
};

export type CampaignRecipientRow = {
  id: number;
  email: string;
  variant: string;
  status: string;
  sentAt?: string | null;
  openedAt?: string | null;
  openCount: number;
  clickedAt?: string | null;
  clickCount: number;
};

export type CreateCampaignPayload = {
  name: string;
  subjectA: string;
  subjectB?: string | null;
  htmlA?: string | null;
  htmlB?: string | null;
  templateId?: number | null;
  segment?: string;
  testPercent?: number;
  waitHours?: number;
  winnerCriteria?: string;
  recipientLimit?: number;
  scheduledAt?: string | null;
  dryRun?: boolean;
};

export async function listCampaigns(): Promise<CampaignListItem[]> {
  const data = await adminFetch<CampaignListItem[]>('/api/admin/campaigns');
  return Array.isArray(data) ? data : [];
}

export async function getCampaign(id: number): Promise<Campaign> {
  return (await adminFetch<Campaign>(`/api/admin/campaigns/${id}`))!;
}

export async function createCampaign(payload: CreateCampaignPayload): Promise<{ ok: boolean; id: number }> {
  return (await adminFetch<{ ok: boolean; id: number }>('/api/admin/campaigns', { method: 'POST', body: payload }))!;
}

export async function updateCampaign(id: number, payload: Partial<CreateCampaignPayload>): Promise<{ ok: boolean }> {
  return (await adminFetch<{ ok: boolean }>(`/api/admin/campaigns/${id}`, { method: 'PUT', body: payload }))!;
}

export async function deleteCampaign(id: number): Promise<{ ok: boolean }> {
  return (await adminFetch<{ ok: boolean }>(`/api/admin/campaigns/${id}`, { method: 'DELETE' }))!;
}

export async function sendCampaign(id: number): Promise<{ ok: boolean; status: string }> {
  return (await adminFetch<{ ok: boolean; status: string }>(`/api/admin/campaigns/${id}/send`, { method: 'POST', body: {} }))!;
}

export async function cancelCampaign(id: number): Promise<{ ok: boolean }> {
  return (await adminFetch<{ ok: boolean }>(`/api/admin/campaigns/${id}/cancel`, { method: 'POST', body: {} }))!;
}

export async function previewCampaign(id: number, tracking = false): Promise<{ subjectA: string; htmlA: string; subjectB?: string; htmlB?: string }> {
  const qs = tracking ? '?tracking=true' : '';
  return (await adminFetch<any>(`/api/admin/campaigns/${id}/preview${qs}`, { method: 'POST', body: {} }))!;
}

export async function sendTestEmail(id: number, to: string[]): Promise<{ sent: number }> {
  return (await adminFetch<{ sent: number }>(`/api/admin/campaigns/${id}/test-send`, { method: 'POST', body: { to } }))!;
}

export async function getCampaignStats(id: number): Promise<CampaignStats> {
  return (await adminFetch<CampaignStats>(`/api/admin/campaigns/${id}/stats`))!;
}

export async function getCampaignRecipients(id: number, page = 1, pageSize = 25): Promise<{ rows: CampaignRecipientRow[]; total: number }> {
  return (await adminFetch<{ rows: CampaignRecipientRow[]; total: number }>(`/api/admin/campaigns/${id}/recipients?page=${page}&pageSize=${pageSize}`))!;
}

export async function estimateRecipients(segment?: string): Promise<{ count: number }> {
  const qs = segment ? `?segment=${encodeURIComponent(segment)}` : '';
  return (await adminFetch<{ count: number }>(`/api/admin/campaigns/estimate${qs}`))!;
}

// ----- Engagement dashboard -----

export async function getEngagementOverview(days = 30): Promise<EngagementOverview> {
  return (await adminFetch<EngagementOverview>(`/api/admin/campaigns/overview?days=${days}`))!;
}

export async function getSendsOverTime(days = 30): Promise<SendsOverTimePoint[]> {
  const data = await adminFetch<SendsOverTimePoint[]>(`/api/admin/campaigns/sends-over-time?days=${days}`);
  return Array.isArray(data) ? data : [];
}

export async function getSchedulerStatus(days = 30): Promise<SchedulerStatus[]> {
  const data = await adminFetch<SchedulerStatus[]>(`/api/admin/campaigns/schedulers?days=${days}`);
  return Array.isArray(data) ? data : [];
}

export async function getAudienceHealth(): Promise<AudienceHealth> {
  return (await adminFetch<AudienceHealth>(`/api/admin/campaigns/suppression`))!;
}

export async function getSegmentReach(): Promise<SegmentReach[]> {
  const data = await adminFetch<SegmentReach[]>(`/api/admin/campaigns/by-segment`);
  return Array.isArray(data) ? data : [];
}

// Drip streams (onboarding reminder etc.) deliver through DripSchedules, not
// the Campaigns table, so the campaigns list alone under-reports the program.
export type DripCampaignSummary = {
  campaignId: string;
  templateKey: string;
  total: number;
  sent: number;
  pending: number;
  skipped: number;
  cancelled: number;
  errors: number;
  firstScheduledFor: string;
  lastActivity: string;
};

export async function getDripSummary(take = 20): Promise<DripCampaignSummary[]> {
  const data = await adminFetch<{ rows: DripCampaignSummary[] }>(`/api/admin/drips/summary?take=${take}`);
  return Array.isArray(data?.rows) ? data!.rows : [];
}
